import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { initSentry } from '@/lib/sentry/init';
import { initClarity } from '@/lib/clarity/init';
import './index.css';
import {
  RouterProvider,
  createRouter,
  parseSearchWith,
} from '@tanstack/react-router';
import { routeTree } from './routeTree.gen';
import { NotFoundPage } from './pages/not-found-page';
import { PageLoader } from '@/components/molecules/page-loader';
import { ErrorFallback } from '@repo/ui/components/molecules/error-fallback';
import { sanitizeBootUrl } from '@/lib/url/sanitize-boot-url';
import { captureImpersonationHandoffToken } from '@/lib/auth/impersonation-handoff';
import { protectPublicDocumentPage } from '@/lib/url/public-document-privacy';
import { ROUTER_ERROR_OPTIONS } from '@repo/ui/lib/router-error-options';
import { parseSearchValue } from '@/lib/url/parse-search-value';
import { AppProviders } from '@/providers/app-providers';

sanitizeBootUrl();
// Il token di passaggio dell'impersonation esce dall'indirizzo prima che
// Sentry e Clarity leggano la pagina.
captureImpersonationHandoffToken();
// Prima di ogni richiesta: il token di un documento pubblico (link del
// rapportino, firma del contratto) non esce come Referer, e la pagina non
// entra nelle registrazioni di sessione (né Clarity né replay di Sentry).
const isPublicDocumentPage = protectPublicDocumentPage();
initSentry({ publicTokenPage: isPublicDocumentPage });
if (!isPublicDocumentPage) initClarity();

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultStaleTime: 5000,
  defaultPendingMs: 150,
  defaultPendingMinMs: 300,
  defaultPendingComponent: PageLoader,
  scrollRestoration: true,
  trailingSlash: 'never',
  defaultNotFoundComponent: NotFoundPage,
  ...ROUTER_ERROR_OPTIONS,
  parseSearch: parseSearchWith(parseSearchValue),
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
    <AppProviders publicTokenPage={isPublicDocumentPage}>
      <RouterProvider router={router} />
    </AppProviders>
  </Sentry.ErrorBoundary>
);
